import { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { EmployeePickerModal } from '../../../../common/selectComponents/EmployeePicker';

export function AddEditFormMemo({
	isOpen,
	onClose,
	memoTypeId, setMemoTypeId,
	memoTypes = [],
	dateFrom, setDateFrom,
	dateTo, setDateTo,
	description, setDescription,
	content, setContent,
	existingImage,
	// ─── CC props ─────────────────────────────────────────────────────────────
	ccList = [],
	setCcList,
	// ─────────────────────────────────────────────────────────────────────────
	// ─── PATCHED: isActive prop ───────────────────────────────────────────────
	isActive, setIsActive,
	// ─────────────────────────────────────────────────────────────────────────
	handleSubmit,
	modalName,
}) {
	const [errorState, setErrorState] = useState(false);
	const [showCcPicker, setShowCcPicker] = useState(false);

	const submit = (e) => {
		if (!memoTypeId || !dateFrom || !dateTo) {
			setErrorState(true);
			return;
		}
		handleSubmit(e);
		setErrorState(false);
		handleClose();
	};

	const handleClose = () => {
		onClose();
		setMemoTypeId("");
		setDateFrom("");
		setDateTo("");
		setDescription("");
		setContent(null);
		setErrorState(false);
	};

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		setContent(file ?? null);
	};

	// ─── CC handlers ──────────────────────────────────────────────────────────
	const handleCcEmployeeSelected = (employee) => {
		// Prevent duplicates
		if (!ccList.find(e => e.id === employee.id)) {
			setCcList([...ccList, { id: employee.id, fullName: employee.fullName, employeeId: employee.employeeId }]);
		}
		setShowCcPicker(false);
	};

	const handleRemoveCc = (employeeId) => {
		setCcList(ccList.filter(e => e.id !== employeeId));
	};
	// ──────────────────────────────────────────────────────────────────────────

	const previewSrc = content
		? URL.createObjectURL(content)
		: existingImage
			? `data:image/jpeg;base64,${existingImage}`
			: null;

	return (
		<>
			<div className={`modal-overlay ${isOpen ? 'open' : ''}`}>
				{isOpen && (
					<div className="modal">
						<div className="popUp-header row">
							<div className="popUp-title">
								<h3 style={{ color: "#668fca" }}>{modalName}</h3>
							</div>
							<div className="popUp-close">
								<CloseIcon onClick={handleClose} />
							</div>
						</div>

						<div className="popUp-body">

							{/* Memo Type */}
							<div className="popUp-item info-item column">
								<div className="info-item-label">Memo Type</div>
								<div className="info-item-input">
									<select
										value={memoTypeId}
										onChange={(e) => setMemoTypeId(e.target.value)}
										style={{ width: "100%" }}
									>
										<option value="">-- Select Memo Type --</option>
										{memoTypes.map((mt) => (
											<option key={mt.id} value={mt.id}>{mt.name}</option>
										))}
									</select>
								</div>
								<span style={{
									visibility: !memoTypeId && errorState ? "visible" : "hidden",
									color: "#ff6565", fontSize: "12px"
								}}>
									Please select a memo type
								</span>
							</div>

							{/* Date From */}
							<div className="popUp-item info-item column">
								<div className="info-item-label">Date From</div>
								<div className="info-item-input">
									<input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
								</div>
								<span style={{
									visibility: !dateFrom && errorState ? "visible" : "hidden",
									color: "#ff6565", fontSize: "12px"
								}}>
									Please fill up this field
								</span>
							</div>

							{/* Date To */}
							<div className="popUp-item info-item column">
								<div className="info-item-label">Date To</div>
								<div className="info-item-input">
									<input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
								</div>
								<span style={{
									visibility: !dateTo && errorState ? "visible" : "hidden",
									color: "#ff6565", fontSize: "12px"
								}}>
									Please fill up this field
								</span>
							</div>

							{/* Description */}
							<div className="popUp-item info-item column">
								<div className="info-item-label">Description</div>
								<div className="info-item-input">
									<textarea
										value={description}
										placeholder="Enter description"
										rows={4}
										onChange={(e) => setDescription(e.target.value)}
										style={{ width: "100%", resize: "vertical" }}
									/>
								</div>
							</div>

							{/* Attachment */}
							<div className="popUp-item info-item column">
								<div className="info-item-label">Attachment</div>
								<div className="info-item-input">
									<input
										type="file"
										accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
										onChange={handleFileChange}
									/>
								</div>
								{previewSrc && (
									<div style={{ marginTop: "8px" }}>
										<img
											src={previewSrc}
											alt="attachment preview"
											style={{ width: "150px", height: "auto", borderRadius: "4px", border: "1px solid #ddd" }}
										/>
										{content && (
											<span style={{ display: "block", fontSize: "12px", color: "#668fca", marginTop: "4px" }}>
												Selected: {content.name}
											</span>
										)}
									</div>
								)}
							</div>

							{/* ─── CC Section ─────────────────────────────────────────────────── */}
							<div className="popUp-item info-item column">
								<div className="info-item-label" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
									<span>CC</span>
									<button
										type="button"
										onClick={() => setShowCcPicker(true)}
										style={{
											fontSize: "11px",
											padding: "2px 10px",
											borderRadius: "4px",
											border: "1px solid #668fca",
											color: "#668fca",
											background: "white",
											cursor: "pointer"
										}}
									>
										+ Add
									</button>
								</div>
								<div className="info-item-input">
									{ccList.length === 0 ? (
										<span style={{ fontSize: "12px", color: "#aaa" }}>No CC recipients added.</span>
									) : (
										<div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
											{ccList.map((emp) => (
												<div
													key={emp.id}
													style={{
														display: "flex",
														alignItems: "center",
														gap: "4px",
														backgroundColor: "#eef2ff",
														border: "1px solid #c7d2fe",
														borderRadius: "999px",
														padding: "2px 10px 2px 10px",
														fontSize: "12px",
														color: "#3730a3"
													}}
												>
													<span>{emp.fullName}</span>
													<button
														type="button"
														onClick={() => handleRemoveCc(emp.id)}
														style={{
															background: "none",
															border: "none",
															cursor: "pointer",
															color: "#818cf8",
															fontSize: "14px",
															lineHeight: 1,
															padding: "0 0 0 2px"
														}}
													>
														✕
													</button>
												</div>
											))}
										</div>
									)}
								</div>
							</div>
							{/* ──────────────────────────────────────────────────────────────── */}

							{/* ─── PATCHED: Active checkbox ────────────────────────────────────── */}
							<div className="popUp-item info-item row" style={{ alignItems: "center" }}>
								<div className="info-item-label" style={{ paddingRight: "10px" }}>Is Active?</div>
								<div className="info-item-input">
									<input type="checkbox"
										id="isActive-checkbox"
										className="checkbox"
										name=""
										checked={isActive !== undefined ? isActive : true}
										onChange={(e) => setIsActive(e.target.checked)} />
								</div>
							</div>
							{/* ──────────────────────────────────────────────────────────────── */}

						</div>

						<div className="buttons row" style={{ justifyContent: "end", padding: "20px 40px" }}>
							<button id="add-memo" className="save row" onClick={(e) => submit(e)}>
								SAVE
							</button>
						</div>
					</div>
				)}
			</div>

			{/* ─── CC Employee Picker ───────────────────────────────────────────────── */}
			<EmployeePickerModal
				isOpen={showCcPicker}
				onClose={() => setShowCcPicker(false)}
				setData={handleCcEmployeeSelected}
			/>
			{/* ──────────────────────────────────────────────────────────────────────── */}
		</>
	);
}

export function DeleteForm({ isOpen, onClose, handleDelete }) {
	const handleSubmit = () => {
		handleDelete();
		handleClose();
	};

	const handleClose = () => {
		onClose();
	};

	return (
		<div className={`modal-overlay ${isOpen ? 'open' : ''}`}>
			{isOpen && (
				<div className="modal">
					<div className="popUp-header row">
						<div className="popUp-title">
							<h3 style={{ color: "#668fca" }}>Delete!</h3>
						</div>
						<div className="popUp-close">
							<CloseIcon onClick={handleClose} />
						</div>
					</div>
					<div className="popUp-body" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
						<span>Are you sure you want to delete this memo?</span>
					</div>
					<div className="buttons row" style={{ justifyContent: "center", padding: "40px 40px 20px 40px" }}>
						<button id="delete-memo" className="save row" onClick={handleSubmit} style={{ marginRight: "5px" }}>
							DELETE
						</button>
						<button id="cancel-delete-memo" className="cancel row" onClick={handleClose}>
							CANCEL
						</button>
					</div>
				</div>
			)}
		</div>
	);
}